import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getFirebaseAuthToken } from '../lib/firebaseAuth';
import { FREE_REQUESTS_PER_MONTH } from '../config/constants';

const getApiUrl = () => process.env.REACT_APP_API_URL || 'https://api.novembr.dev';

const getAuthToken = async () => getFirebaseAuthToken();

export interface BillingSettings {
  id: string;
  user_id: string;
  spending_limit_usd: number;
  alert_threshold_percent: number;
  auto_suspend: boolean;
  created_at: string;
  updated_at: string;
}

export interface PaymentMethod {
  id: string;
  user_id: string;
  stripe_payment_method_id: string;
  type: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface BillingInvoice {
  id: string;
  user_id: string;
  stripe_invoice_id?: string;
  amount_usd: number;
  status: 'pending' | 'paid' | 'failed' | 'void';
  billing_period_start: string;
  billing_period_end: string;
  created_at: string;
  updated_at: string;
}

export interface TriggerBillingJobResponse {
  success: boolean;
  message: string;
  invoice?: {
    id: string;
    amount: number;
    status: string;
    hosted_invoice_url?: string;
    payment_status?: string;
  };
  usage?: {
    totalCost: number;
    totalRequests: number;
    period: {
      start: string;
      end: string;
    };
  };
  result?: any;
  error?: string;
}

export const getUserBillingSettings = async (userId: string): Promise<BillingSettings | null> => {
  const ref = doc(db, 'billing_settings', userId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) {
    return null;
  }
  return { id: snapshot.id, ...(snapshot.data() as Omit<BillingSettings, 'id'>) };
};

export const updateBillingSettings = async (
  userId: string,
  settings: Partial<Omit<BillingSettings, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
): Promise<BillingSettings> => {
  const now = new Date().toISOString();
  const existing = await getUserBillingSettings(userId);
  const payload = {
    user_id: userId,
    spending_limit_usd: settings.spending_limit_usd ?? existing?.spending_limit_usd ?? 50,
    alert_threshold_percent: settings.alert_threshold_percent ?? existing?.alert_threshold_percent ?? 80,
    auto_suspend: settings.auto_suspend ?? existing?.auto_suspend ?? true,
    created_at: existing?.created_at || now,
    updated_at: now,
  };
  await setDoc(doc(db, 'billing_settings', userId), payload, { merge: true });
  return { id: userId, ...payload };
};

export const getUserPaymentMethods = async (userId: string): Promise<PaymentMethod[]> => {
  const snapshot = await getDocs(query(collection(db, 'payment_methods'), where('user_id', '==', userId)));
  return snapshot.docs
    .map((row) => ({ id: row.id, ...(row.data() as Omit<PaymentMethod, 'id'>) }))
    .sort((a, b) => Number(b.is_default) - Number(a.is_default));
};

export const getUserBillingInvoices = async (userId: string): Promise<BillingInvoice[]> => {
  const snapshot = await getDocs(query(collection(db, 'billing_invoices'), where('user_id', '==', userId)));
  return snapshot.docs
    .map((row) => ({ id: row.id, ...(row.data() as Omit<BillingInvoice, 'id'>) }))
    .sort((a, b) => new Date(b.billing_period_start).getTime() - new Date(a.billing_period_start).getTime());
};

export const checkSpendingLimit = async (userId: string): Promise<boolean> => {
  const settings = await getUserBillingSettings(userId);
  if (!settings || !settings.auto_suspend || !settings.spending_limit_usd) {
    return true;
  }

  const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
  const logs = await getDocs(query(collection(db, 'api_usage_logs'), where('user_id', '==', userId)));
  const currentSpending = logs.docs
    .map((item) => item.data())
    .filter((row) => row.created_at >= startOfMonth)
    .reduce((sum, row) => sum + (Number(row.cost_usd) || 0), 0);

  return currentSpending < settings.spending_limit_usd;
};

export const getFreeRequestsRemaining = async (userId: string): Promise<number> => {
  const profile = await getSubscriptionInfo(userId);
  const freeLimit = profile && (profile.subscription_status === 'active' || profile.subscription_status === 'cancelled') ? 600 : FREE_REQUESTS_PER_MONTH;
  const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
  const endOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString();
  const logs = await getDocs(query(collection(db, 'api_usage_logs'), where('user_id', '==', userId)));
  const monthlyCount = logs.docs
    .map((row) => row.data())
    .filter((row) => row.created_at >= startOfMonth && row.created_at < endOfMonth).length;

  return Math.max(0, freeLimit - monthlyCount);
};

export const getUserProfile = async (userId: string): Promise<{ stripe_customer_id?: string } | null> => {
  const snapshot = await getDoc(doc(db, 'user_profiles', userId));
  if (!snapshot.exists()) {
    return null;
  }
  return snapshot.data() as { stripe_customer_id?: string };
};

export const upsertUserProfile = async (userId: string, stripeCustomerId?: string): Promise<void> => {
  await setDoc(
    doc(db, 'user_profiles', userId),
    { user_id: userId, stripe_customer_id: stripeCustomerId, updated_at: new Date().toISOString() },
    { merge: true }
  );
};

export const createStripeCustomer = async (userId: string, email: string, name?: string): Promise<string> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/create-customer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ email, name }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create customer');
  }
  const data = await response.json();
  return data.customer_id;
};

export const createCustomerPortalSession = async (_userId: string): Promise<string> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/portal-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create portal session');
  }
  const data = await response.json();
  return data.url;
};

export const getServerPaymentMethods = async (userId: string): Promise<PaymentMethod[]> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/payment-methods`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  const data = await response.json();
  return data.payment_methods.map((pm: any) => ({
    id: pm.id,
    user_id: userId,
    stripe_payment_method_id: pm.id,
    type: pm.type,
    brand: pm.card?.brand,
    last4: pm.card?.last4,
    exp_month: pm.card?.exp_month,
    exp_year: pm.card?.exp_year,
    is_default: pm.is_default,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }));
};

export const triggerBillingJob = async (
  _userId: string,
  testMode: boolean = true,
  billingPeriodStart?: string,
  billingPeriodEnd?: string
): Promise<TriggerBillingJobResponse> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/trigger-billing-job`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({
      test_mode: testMode,
      billing_period_start: billingPeriodStart,
      billing_period_end: billingPeriodEnd,
    }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP ${response.status}`);
  }
  return response.json();
};

export const attachPaymentMethod = async (
  userId: string,
  paymentMethodId: string,
  setAsDefault: boolean = true
): Promise<PaymentMethod> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/payment-methods`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ payment_method_id: paymentMethodId, set_as_default: setAsDefault }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to attach payment method');
  }
  const data = await response.json();
  return {
    id: data.payment_method.id,
    user_id: userId,
    stripe_payment_method_id: data.payment_method.id,
    type: data.payment_method.type,
    brand: data.payment_method.card?.brand,
    last4: data.payment_method.card?.last4,
    exp_month: data.payment_method.card?.exp_month,
    exp_year: data.payment_method.card?.exp_year,
    is_default: data.payment_method.is_default,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
};

export const getServerBillingInvoices = async (userId: string, limit: number = 10): Promise<BillingInvoice[]> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/invoices?limit=${limit}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch invoices');
  }
  const data = await response.json();
  return data.invoices.map((invoice: any) => ({
    id: invoice.id,
    user_id: userId,
    stripe_invoice_id: invoice.id,
    amount_usd: invoice.amount_paid / 100,
    status: invoice.status,
    billing_period_start: new Date(invoice.period_start * 1000).toISOString(),
    billing_period_end: new Date(invoice.period_end * 1000).toISOString(),
    created_at: new Date(invoice.created * 1000).toISOString(),
    updated_at: new Date(invoice.created * 1000).toISOString(),
  }));
};

export const createUsageInvoice = async (
  _userId: string,
  billingPeriodStart: string,
  billingPeriodEnd: string
): Promise<any> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/create-invoice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ billing_period_start: billingPeriodStart, billing_period_end: billingPeriodEnd }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create invoice');
  }
  return response.json();
};

export interface SubscriptionInfo {
  subscription_status: 'free' | 'active' | 'cancelled' | 'past_due';
  subscription_plan: string | null;
  subscription_start_date: string | null;
  subscription_billing_date: number | null;
  stripe_subscription_id: string | null;
  subscription_cancelled_at: string | null;
  subscription_current_period_end?: string | null;
}

export const getSubscriptionInfo = async (userId: string): Promise<SubscriptionInfo | null> => {
  const snapshot = await getDoc(doc(db, 'user_profiles', userId));
  if (!snapshot.exists()) {
    return {
      subscription_status: 'free',
      subscription_plan: null,
      subscription_start_date: null,
      subscription_billing_date: null,
      stripe_subscription_id: null,
      subscription_cancelled_at: null,
      subscription_current_period_end: null,
    };
  }

  const data = snapshot.data();
  return {
    subscription_status: data.subscription_status || 'free',
    subscription_plan: data.subscription_plan || null,
    subscription_start_date: data.subscription_start_date || null,
    subscription_billing_date: data.subscription_billing_date || null,
    stripe_subscription_id: data.stripe_subscription_id || null,
    subscription_cancelled_at: data.subscription_cancelled_at || null,
    subscription_current_period_end: data.subscription_current_period_end || null,
  };
};

export const hasActiveSubscription = async (userId: string): Promise<boolean> => {
  const info = await getSubscriptionInfo(userId);
  if (!info) return false;
  if (info.subscription_status === 'active') return true;
  if (info.subscription_status === 'cancelled' && info.subscription_current_period_end) {
    return new Date(info.subscription_current_period_end).getTime() > Date.now();
  }
  return false;
};

export const createSubscriptionCheckout = async (_userId: string): Promise<string> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/create-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create subscription checkout');
  }
  const data = await response.json();
  return data.checkout_url;
};

export const syncSubscriptionStatus = async (_userId: string): Promise<any> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/sync-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP ${response.status}`);
  }
  return response.json();
};

export const cancelSubscription = async (_userId: string): Promise<void> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/cancel-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to cancel subscription');
  }
};

export const resumeSubscription = async (_userId: string): Promise<void> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('User not authenticated');
  const response = await fetch(`${getApiUrl()}/api/billing/resume-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to resume subscription');
  }
};

export const setStripeCustomerId = async (userId: string, stripeCustomerId: string): Promise<void> => {
  await setDoc(
    doc(db, 'user_profiles', userId),
    { user_id: userId, stripe_customer_id: stripeCustomerId, updated_at: new Date().toISOString() },
    { merge: true }
  );
};

export const setSubscriptionFields = async (
  userId: string,
  data: Partial<SubscriptionInfo>
): Promise<void> => {
  await updateDoc(doc(db, 'user_profiles', userId), {
    ...data,
    updated_at: new Date().toISOString(),
  });
};
